//#region Imports

import { Component, Input, Signal, computed } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { IconComponent } from '../icon/icon.component';
import { Menu } from './models/menu';
import { MenuService } from './services/menu.service';
import { PageService } from '../../services/page/page.service';
import { PageKey } from '../../services/page/models/page.key';
import { ThemeService } from '../../services/theme/theme.service';
import { ThemeEnum } from '../../services/theme/models/enums/theme.enum';
import { AuthService } from '../../services/auth/auth.service';
import { ModalService } from '../../services/modal/modal.service';
import { createCancelConfirmButtons, openConfirmModal } from '../../../helpers/modal.helper';
import { ButtonTypeEnum } from '../button/types/button-type.enum';
import { MeService } from '../../services/me/me.service';
import { AvatarComponent } from '../avatar/avatar.component';
import { getAppVersion } from '../../utils/app-version.util';

//#endregion

@Component({
  selector: 'l-menu',
  standalone: true,
  templateUrl: './menu.component.html',
  imports: [
      RouterLink,
      AvatarComponent,
      IconComponent,
      NgIf,
      NgClass,
      NgForOf
  ],
  animations: [
    trigger('submenuExpand', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('250ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('200ms cubic-bezier(0.4, 0, 1, 1)', style({ height: '0', opacity: 0 })),
      ]),
    ]),
    trigger('chevronRotate', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(180deg)' })),
      transition('collapsed <=> expanded', animate('250ms cubic-bezier(0.4, 0, 0.2, 1)')),
    ]),
  ],
})
export class MenuComponent {

  //#region Constructor

  constructor(
    private readonly menuService: MenuService,
    private readonly pageService: PageService,
    private readonly themeService: ThemeService,
    private readonly authService: AuthService,
    private readonly modalService: ModalService,
    private readonly meService: MeService,
  ) {}

  //#endregion

  //#region Inputs

  @Input({ required: true })
  public menus: Menu[] = [];

  //#endregion

  //#region Public Properties

  public activatedIndex = computed((): number => {
    const currentPageKey = this.pageService.page();

    if (!currentPageKey)
      return -1;

    return this.menus.findIndex(menu => {
      return (
        menu.key === currentPageKey ||
        menu.submenus?.find(submenu => submenu.key === currentPageKey)
      );
    });
  });

  public shortMode: Signal<boolean> = this.menuService.short;

  public pageKey: Signal<PageKey | null> = this.pageService.page;

  public theme: Signal<ThemeEnum> = this.themeService.theme;

  public readonly ThemeEnum = ThemeEnum;

  public readonly appVersion: string = getAppVersion();

  public readonly me = this.meService.me;

  //#endregion

  //#region Public Methods

  public toggleShortMode(): void {
    this.menuService.short = !this.menuService.short();
  }

  public toggleMenu(index: number): void {
    const menu = this.menus[index];

    if (!menu?.submenus?.length)
      return;

    this.menus[index].opened = !this.menus[index].opened
  }

  public isSubmenuActive(submenu: Menu): boolean {
    return submenu.key === this.pageKey();
  }

  public isParentExpanded(menu: Menu): boolean {
    if (menu.opened) {
      return true;
    }

    return menu.submenus?.some((submenu) => this.isSubmenuActive(submenu)) ?? false;
  }

  public toggleTheme(): void {
    const newTheme = this.themeService.theme() === ThemeEnum.DARK ? ThemeEnum.LIGHT : ThemeEnum.DARK;
    this.themeService.theme = newTheme;
  }

  public setTheme(theme: ThemeEnum): void {
    this.themeService.theme = theme;
  }

  public logout(): void {
    openConfirmModal(
      this.modalService,
      'fit-content',
      {
        title: 'Sair da conta',
        description: 'Tem certeza que deseja encerrar sua sessão?',
        buttons: createCancelConfirmButtons('Sair', ButtonTypeEnum.DANGER),
      },
      async () => {
        await this.authService.logout();
      },
    );
  }

  //#endregion

}
